import classNames from 'classnames';
import { isPast, min, subHours } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS, LabelXS } from '#src/atoms/typography/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useConversionCredits } from '#src/hooks/useConversionCredits.tsx';
import { Sum } from '#src/hooks/useMonetaryAmount.ts';

import { MarketCreditExpirationGauge } from './MarketCreditExpirationGauge/index.tsx';

const Root = styled(Horizontal)`
  padding: var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);

  &.lastDay {
    background: linear-gradient(
      96deg,
      rgba(240, 201, 86, 0.16) 0%,
      rgba(240, 201, 86, 0.03) 100%
    );
  }
  &.sidebarCollapsed {
    border: none;
    justify-content: center;
  }

  &:not(.sidebarCollapsed):hover {
    background-color: var(--c-nd-50);
    border-color: var(--c-nd-300);
  }
  transition:
    300ms background-color ease-in-out,
    300ms border-color ease-in-out;
`;
const Middle = styled(Vertical)`
  flex: 1;
  text-align: left;
`;
const MarketCreditsAmount = styled(Horizontal).attrs({ center: true })`
  background: var(--c-nd-50);
  border-radius: 50%;
  width: var(--double-unit);
  height: var(--double-unit);
`;

type Props = { sport: Sport; sidebarCollapsed?: boolean };
export const MarketCredits = ({ sport, sidebarCollapsed }: Props) => {
  const { formatDistanceToNow } = useIntlContext();
  const { conversionCredits = [], groupedConversionCredits } =
    useConversionCredits({ sport });
  const conversionCredit = conversionCredits.at(0);
  const maxAmount = Sum(
    conversionCredits.filter(cc => !cc.disabled).map(cc => cc.maxDiscount)
  );
  const totalAmount = Sum(
    conversionCredits.filter(cc => !cc.disabled).map(cc => cc.totalDiscount)
  );
  const { main } = useAmountWithConversion({
    monetaryAmount: maxAmount,
  });

  if (!conversionCredit) {
    return null;
  }

  const minEndDate = min(conversionCredits.map(cc => cc.endDate));

  const timeLeft = formatDistanceToNow(minEndDate, { addSuffix: false });

  const lastDay = isPast(subHours(minEndDate, 24));

  const nbConversionCredits = Object.values(groupedConversionCredits).reduce(
    (acc, cc) => acc + cc.length,
    0
  );
  return (
    <Root className={classNames({ lastDay, sidebarCollapsed })}>
      <MarketCreditExpirationGauge
        percentage={(maxAmount.eurCents / totalAmount.eurCents) * 100}
      />
      {!sidebarCollapsed && (
        <>
          <Middle gap={0.5}>
            <Horizontal>
              <LabelS bold>{main}</LabelS>
            </Horizontal>
            <LabelS color={lastDay ? 'var(--c-yellow-400)' : 'var(--c-nd-600)'}>
              <FormattedMessage
                defaultMessage="{timeLeft} left"
                values={{ timeLeft }}
              />
            </LabelS>
          </Middle>
          <Horizontal gap={0.5}>
            {nbConversionCredits > 1 && (
              <MarketCreditsAmount>
                <LabelXS>{nbConversionCredits}</LabelXS>
              </MarketCreditsAmount>
            )}
            <FontAwesomeIcon
              icon={iconChevronRight}
              size="sm"
              color="var(--c-nd-600)"
            />
          </Horizontal>
        </>
      )}
    </Root>
  );
};
